/**
 * lib/gl.ts
 * ------------------------------------------------------------------
 * THE GENERAL LEDGER — Phase 2 of FINANCE-DESIGN.md.
 *
 * Types, the posting map, and the journal builder. Pure module: no React,
 * no Firestore reads — so the posting engine, the screens, the seed
 * scripts and (later) Cloud Functions can all share one definition of
 * what a balanced journal is.
 *
 * The five rules this file exists to enforce (FINANCE-DESIGN.md §5):
 *
 *  1. Journals are APPEND-ONLY. Never edit a posted journal — reverse it
 *     with a contra journal. `reverseJournal` is the only way back.
 *  2. Ids are DETERMINISTIC off the source document, so re-posting the
 *     same invoice twice overwrites rather than double-counts. This is
 *     the billing-run idempotency pattern applied to the ledger.
 *  3. Balances are COMPUTED, never stored. There is deliberately no
 *     `balance` field on GlAccount — a trial balance is a reduce over
 *     journal lines. A stored balance is a balance that drifts.
 *  4. The GL is DERIVED, never authoritative. The invoice is the truth
 *     about the invoice; if they disagree, the sub-ledger wins and you
 *     re-post.
 *  5. assertBalanced() before EVERY write. A journal that does not
 *     balance is never persisted.
 *
 * Accounts are resolved by `systemKey`, NEVER by hardcoded code, because
 * the school will renumber its chart to match the bursar's Pastel
 * masterfile. Same lesson as the report generators keying on division
 * rather than sniffing a class name.
 *
 * VAT: settled as a SWITCH, not a feature (lib/vat.ts). Education is an
 * exempt supply, so a school billing only tuition does not register and
 * cannot reclaim its input tax; one with a uniform shop or hall hire may
 * be registered on a mixed basis. It went in exactly as this comment
 * predicted — additional journal lines against the VAT_INPUT /
 * VAT_OUTPUT system keys, and no field on JournalLine changed.
 * ------------------------------------------------------------------ */

import { round2 } from '@/lib/hrPayroll';

export const GL_COLLECTIONS = {
  accounts: 'financeGlAccounts',
  journals: 'financeGlJournals',
  postingMap: 'financeGlPostingMap',
  pastelSnapshots: 'financePastelSnapshots',
  periods: 'financeGlPeriods',
} as const;

/** Singleton doc id for the posting map. */
export const POSTING_MAP_ID = 'current';

/* ============================== accounts ============================== */

export type AccountType = 'asset' | 'liability' | 'equity' | 'income' | 'expense';

/**
 * Stable handles the posting engine resolves accounts by. A SystemKey is a
 * promise that some code path needs this account to exist; the code attached
 * to it is the school's business and may be renumbered freely.
 */
export type SystemKey =
  | 'BANK_MAIN' | 'BANK_SECOND' | 'CASH_ON_HAND'
  // Petty cash is NOT "cash on hand". Cash on hand is the till and the fee
  // receipts banked in bulk — it runs to millions here. A petty float is a tin
  // holding a couple of thousand, and reconciling the two against each other
  // makes the petty-cash control break by the entire fee receipts balance.
  | 'PETTY_CASH'
  | 'AR_FEES' | 'AP_TRADE'
  // A salary advance or staff loan is an ASSET — money the school is owed
  // back, not an expense. Phase 6 disburses them through a voucher, so the
  // accrual debits this and the payroll recovery credits it; without a
  // separate account the disbursement would land in salaries and the school
  // would appear to have spent money it is going to get back.
  | 'STAFF_ADVANCES'
  | 'PAYE_PAYABLE' | 'NET_SALARIES_PAYABLE' | 'MEDICAL_AID_PAYABLE'
  | 'FEES_IN_ADVANCE' | 'GRATUITY_PROVISION'
  // Where a cost waiting for its period is parked (lib/deferrals.ts).
  // PREPAYMENTS is an asset — money spent on something not yet consumed;
  // ACCRUALS is a liability — something consumed but not yet billed.
  | 'PREPAYMENTS' | 'ACCRUALS'
  | 'ACCUM_FUND' | 'SURPLUS_CURRENT'
  | 'DISCOUNTS_GRANTED' | 'STOCK_ON_HAND' | 'COST_OF_SALES'
  | 'DEPRECIATION_EXPENSE' | 'SALARIES_EXPENSE' | 'BAD_DEBTS'
  // Where a requisition lands when it is neither capitalised nor otherwise
  // coded. A default that is NAMED beats one that is buried in a function.
  | 'SUNDRY_EXPENSE'
  // VAT, when the school is registered (lib/vat.ts). Both are BALANCES, not
  // tallies: output tax is a liability owed to BURS, input tax an asset
  // reclaimable from it, and the return is read off them so it cannot
  // disagree with the trial balance. Unused while VAT is switched off, which
  // is the default and usually correct — education is an exempt supply.
  | 'VAT_OUTPUT' | 'VAT_INPUT';

export interface GlAccount {
  /** = code. The doc id IS the account code, so a lookup is a get, not a query. */
  id: string;
  code: string;
  name: string;
  type: AccountType;
  /** Grouping for subtotals. A header account is not postable. */
  parentCode?: string;
  systemKey?: SystemKey;
  isPostable: boolean;
  active: boolean;
  sortOrder: number;
  createdAt?: string;
  updatedAt?: string;
  updatedBy?: string;
}

/** Which side increases this account type — drives sign in the trial balance. */
export function normalBalance(type: AccountType): 'dr' | 'cr' {
  return type === 'asset' || type === 'expense' ? 'dr' : 'cr';
}

/* ============================== journals ============================== */

export interface JournalLine {
  accountCode: string;
  dr: number;
  cr: number;
  narration?: string;
  /**
   * Cost-centre tagging — how "what does Secondary cost to run" becomes
   * answerable.
   *
   * `costCentre` is the one the budget and the by-centre report read
   * (lib/budgets.ts); it holds a CostCentre.code. `division` and `department`
   * are free descriptive tags for slicing a report further and are not the
   * budgeting axis — keeping them apart means a school can re-organise its
   * departments without invalidating a year's budget.
   *
   * Absent on most historical lines: dimensions were declared here from the
   * start and never actually set until the cost-centre work, so the by-centre
   * report shows an "unattributed" row rather than pretending otherwise.
   */
  dimension?: { costCentre?: string; division?: string; department?: string };
  /**
   * VAT treatment of this line (lib/vat.ts). Absent on everything posted before
   * the school records a VAT position, which is why the return counts untagged
   * lines rather than assuming they are exempt.
   */
  taxCode?: 'standard' | 'zero' | 'exempt' | 'none';
}

export type JournalSourceType =
  | 'opening' | 'invoice' | 'payment' | 'discount' | 'voucher' | 'payment_batch'
  | 'payslip' | 'depreciation' | 'shop_sale' | 'shop_grn' | 'manual' | 'transfer' | 'reversal'
  | 'year_end'
  // A credit note or bad-debt write-off. Distinct from 'discount' (which is
  // granted when the invoice is raised) and from 'reversal' (which unwinds a
  // journal): an adjustment is a decision made AFTER billing, and the reports
  // need to be able to separate the three.
  | 'adjustment'
  // A petty-cash slip, its reversal, or a count variance. Separate from
  // 'manual' so the small-spend trail can be read on its own — it is the part
  // of the accounts with the least other evidence behind it.
  | 'petty_cash'
  // An accrual or prepayment: the opening entry and each scheduled release
  // (lib/recurring.ts). Its own type so the period-end adjustments can be read
  // apart from ordinary trading.
  | 'deferral'
  // A standing entry posted from a template, once a month, unattended
  // (lib/recurring.ts). Distinct from 'manual' precisely BECAUSE nobody typed
  // it: a reader needs to be able to tell the entries a person decided on
  // from the ones a schedule produced.
  | 'recurring'
  // A cash-book receipt or payment: money through the bank that no invoice or
  // voucher covers — bank charges, interest, a donation. Its own type so the
  // cash book can be read apart from trading.
  | 'cash_book'
  // A debtor or creditor journal — correcting a party's account without
  // inventing an invoice. Distinct from 'adjustment' (a credit note the parent
  // sees) and from 'manual' (a journal with no party at all).
  | 'party_journal';

export interface GlJournal {
  /** Deterministic — see journalIdFor(). */
  id: string;
  journalNo: string;
  date: string;
  fy: string;
  source: { type: JournalSourceType; id: string };
  narration: string;
  lines: JournalLine[];
  totalDr: number;
  /** Must equal totalDr — asserted before write. */
  totalCr: number;
  status: 'posted' | 'reversed';
  /** Journal id this one reverses. */
  reverses?: string;
  reversedBy?: string;
  postedBy: string;
  postedAt: string;
  seedBatch?: string;
}

/** Short prefix per source type — makes an id readable in the console. */
const SOURCE_PREFIX: Record<JournalSourceType, string> = {
  opening: 'open', invoice: 'inv', payment: 'pay', discount: 'disc',
  voucher: 'pv', payment_batch: 'batch', payslip: 'slip',
  depreciation: 'dep', shop_sale: 'shop', shop_grn: 'grn',
  manual: 'man', transfer: 'tfr', reversal: 'rev', year_end: 'ye',
  adjustment: 'adj',
  petty_cash: 'pc',
  deferral: 'def',
  recurring: 'rec',
  cash_book: 'cb',
  party_journal: 'pj',
};

/**
 * The deterministic id. Posting the same source document twice produces the
 * same id, so the second write is an overwrite and the ledger cannot
 * double-count. Manual journals pass their own unique id as sourceId.
 */
export function journalIdFor(type: JournalSourceType, sourceId: string): string {
  return `jnl_${SOURCE_PREFIX[type]}_${sourceId}`;
}

/* ============================== balancing ============================== */

export interface JournalTotals {
  totalDr: number;
  totalCr: number;
  balanced: boolean;
}

/** Sum a journal's sides, rounded to the cent BEFORE comparison — floating
 *  point makes 0.1+0.2 a real risk on a 60-line payroll journal. */
export function totalsFor(lines: JournalLine[]): JournalTotals {
  const totalDr = round2(lines.reduce((a, l) => a + (Number(l.dr) || 0), 0));
  const totalCr = round2(lines.reduce((a, l) => a + (Number(l.cr) || 0), 0));
  return { totalDr, totalCr, balanced: totalDr === totalCr };
}

export class JournalImbalanceError extends Error {
  constructor(public readonly totals: JournalTotals) {
    super(`Journal does not balance: Dr ${totals.totalDr.toFixed(2)} vs Cr ${totals.totalCr.toFixed(2)}`);
    this.name = 'JournalImbalanceError';
  }
}

/**
 * The gate every write passes through. Throws rather than returning false:
 * an unbalanced journal is a bug, and a caller that forgets to check a
 * boolean would persist it.
 */
export function assertBalanced(lines: JournalLine[]): JournalTotals {
  const t = totalsFor(lines);
  if (!t.balanced) throw new JournalImbalanceError(t);
  if (!lines.length) throw new Error('A journal must have at least one line.');
  return t;
}

/** Reject the common data-entry slips before they reach the ledger. */
export function validateLines(lines: JournalLine[]): string[] {
  const errors: string[] = [];
  if (!lines.length) errors.push('Add at least one line.');
  lines.forEach((l, i) => {
    const n = i + 1;
    const dr = Number(l.dr) || 0;
    const cr = Number(l.cr) || 0;
    if (!l.accountCode) errors.push(`Line ${n}: choose an account.`);
    if (dr < 0 || cr < 0) errors.push(`Line ${n}: amounts cannot be negative — use the other side instead.`);
    // A line that is both is almost always a typo, and it hides its own net effect.
    if (dr > 0 && cr > 0) errors.push(`Line ${n}: enter either a debit or a credit, not both.`);
    if (dr === 0 && cr === 0) errors.push(`Line ${n}: enter an amount.`);
  });
  const t = totalsFor(lines);
  if (lines.length && !t.balanced) {
    errors.push(`Out of balance by P${Math.abs(round2(t.totalDr - t.totalCr)).toFixed(2)} — debits ${t.totalDr.toFixed(2)}, credits ${t.totalCr.toFixed(2)}.`);
  }
  return errors;
}

/* ============================== building ============================== */

export interface BuildJournalArgs {
  type: JournalSourceType;
  sourceId: string;
  journalNo: string;
  date: string;
  fy: string;
  narration: string;
  lines: JournalLine[];
  postedBy: string;
  postedAt?: string;
  seedBatch?: string;
}

/** Assemble a posted journal. Throws if it does not balance — so a caller
 *  cannot construct an invalid journal object at all. */
export function buildJournal(a: BuildJournalArgs): GlJournal {
  const lines = a.lines.map((l) => ({ ...l, dr: round2(Number(l.dr) || 0), cr: round2(Number(l.cr) || 0) }));
  const { totalDr, totalCr } = assertBalanced(lines);
  return {
    id: journalIdFor(a.type, a.sourceId),
    journalNo: a.journalNo,
    date: a.date,
    fy: a.fy,
    source: { type: a.type, id: a.sourceId },
    narration: a.narration,
    lines,
    totalDr,
    totalCr,
    status: 'posted',
    postedBy: a.postedBy,
    postedAt: a.postedAt ?? new Date().toISOString(),
    ...(a.seedBatch ? { seedBatch: a.seedBatch } : {}),
  };
}

/**
 * The ONLY way to undo. Produces a contra journal with the sides swapped;
 * the original is left untouched and merely marked `reversed`, so the audit
 * trail shows both what was posted and what corrected it.
 */
export function reverseJournal(
  original: GlJournal,
  opts: { journalNo: string; date: string; fy: string; postedBy: string; reason: string; postedAt?: string },
): GlJournal {
  const lines = original.lines.map((l) => ({ ...l, dr: l.cr, cr: l.dr }));
  const { totalDr, totalCr } = assertBalanced(lines);
  return {
    id: journalIdFor('reversal', original.id),
    journalNo: opts.journalNo,
    date: opts.date,
    fy: opts.fy,
    source: { type: 'reversal', id: original.id },
    narration: `Reversal of ${original.journalNo}: ${opts.reason}`,
    lines,
    totalDr,
    totalCr,
    status: 'posted',
    reverses: original.id,
    postedBy: opts.postedBy,
    postedAt: opts.postedAt ?? new Date().toISOString(),
  };
}

/* ============================== posting map ============================== */

/**
 * One config document mapping the enums the app already carries onto account
 * codes. Small because the existing enums already hold the dimensions — a
 * FeeCategory IS the income line.
 *
 * Values are account CODES (a GlAccount id). Missing keys are a
 * configuration error surfaced at posting time, never a silent skip.
 */
export interface GlPostingMap {
  id: string;
  feeCategory: Record<string, string>;
  paymentMethod: Record<string, string>;
  discountKind: Record<string, string>;
  assetCategory: Record<string, { cost: string; accumDep: string }>;
  payrollLine: Record<string, string>;
  shop: { income: string; cos: string; stock: string };
  updatedAt?: string;
  updatedBy?: string;
}

/** Thrown when a source document references an enum the map has no account for. */
export class PostingMapError extends Error {
  constructor(what: string, key: string) {
    super(`No GL account mapped for ${what} "${key}". Set it in Chart of Accounts → Posting map.`);
    this.name = 'PostingMapError';
  }
}

export function accountFor(map: GlPostingMap | null, what: keyof GlPostingMap, key: string): string {
  const table = map?.[what] as Record<string, string> | undefined;
  const code = table?.[key];
  if (!code) throw new PostingMapError(String(what), key);
  return code;
}

/* ============================== numbering ============================== */

/** JNL/2026/000001 — mirrors invoiceNoFormat so the documents read alike. */
/* ============================ periods ============================
   A month of the ledger, and whether it still accepts postings.

   WHY A LOCK EXISTS AT ALL. Once a month has been reported on — a set of
   management accounts printed, a figure given to the board — a journal
   backdated into it silently changes a number somebody has already acted on.
   The lock is what makes "the March accounts" mean one thing forever.

   ABSENT MEANS OPEN. A school that has never closed a period must not find
   itself unable to post; the lock is something the bursar applies, not a
   state they have to escape. So this collection holds only the months that
   have been CLOSED.
   ================================================================ */

export interface GlPeriod {
  /** '2026-08' — the doc id, and the month it governs. */
  id: string;
  fy: string;
  status: 'closed';
  closedAt: string;
  closedBy?: string;
  closedByName?: string;
  /** Why, for the month somebody asks about a year later. */
  note?: string;
}

/** The period a date belongs to. '2026-08-14' -> '2026-08'. */
export function periodIdFor(dateIso: string): string {
  return String(dateIso ?? '').slice(0, 7);
}

/** Is the month this date falls in closed to new postings? */
export function isPeriodClosed(dateIso: string, closed: Iterable<string>): boolean {
  const want = periodIdFor(dateIso);
  if (!want) return false;
  for (const id of closed) if (id === want) return true;
  return false;
}

/**
 * Why this journal cannot carry this date, or null if it can.
 *
 * Three refusals, in the order a bursar is likely to hit them:
 *
 *   NO DATE — a journal with no date has no period, so nothing can ever
 *   decide whether it is allowed.
 *
 *   CLOSED PERIOD — see above. The message names the month rather than
 *   saying "locked", because the fix is either a different date or reopening
 *   that specific month, and the bursar needs to know which.
 *
 *   THE FUTURE — posting into next month is nearly always a typo in the
 *   year, and it puts a figure into accounts that have not happened. A
 *   genuine future-dated accrual is raised in the month it belongs to.
 */
export function journalDateProblem(
  dateIso: string,
  closedPeriods: Iterable<string>,
  today: Date = new Date(),
): string | null {
  const d = String(dateIso ?? '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return 'Give the journal a date.';

  if (isPeriodClosed(d, closedPeriods)) {
    return `${periodIdFor(d)} is closed. Post this to an open month, or ask for that month to be reopened.`;
  }

  const todayIso = new Date(today.getTime() - today.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 10);
  if (d > todayIso) return 'That date is in the future.';

  return null;
}

export function formatJournalNo(fy: string, seq: number): string {
  return `JNL/${fy}/${String(seq).padStart(6, '0')}`;
}

/** Counter doc id, year-scoped for the same reason invoices are. */
export const journalCounterId = (fy: string) => `journals-${fy}`;
